import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import {
  Configurations,
  SessionStorage,
  SurfAuthenticationService,
  SurfComponentsConstants,
  SurfCookieNames, SurfRedirectUrlCaptureServiceService, SurfUtilService,
  SurfVisibilityConfigService
} from '@surf/surf-components-core';
import { User } from '@tc-core/model/it/codegen/tbx/api/authority/v2/users/user';
import { AppContext } from '@tc-core/model/it/codegen/ui/app-context';
import { Scaffolding } from '@tc-core/model/it/codegen/ui/framework/scaffolding';
import { TCRequestOptions } from '@tc-core/model/it/codegen/ui/request-option';
import { TC } from '@tc-core/util/constants';
import { DataServiceHandler } from '@tc-core/service/service-handlers';
import { LogStore } from '@tc-core/util/framework';
import { ConfigLoader } from '@tc-core/util/framework/config-loader.service';
import { DataKey, DataStore } from '@tc-core/util/framework/data-store.service';
import { CookieService as NGXCoockieServive } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../environments/environment.app';
import { CommonHelper } from '@tc-core/util/helpers';
import { SurfGuestDetailsUtil } from '@surf/surf-guest-details';
import { TcHttpError } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-http-error';
import { TcApiError } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-api-error';

@Injectable({providedIn: 'root'})
export class AppService {

  appContext: AppContext;
  currentURL: string = null;
  scaffStructure: BehaviorSubject<Scaffolding[]> = new BehaviorSubject([]);
  private userNameCookie = 'X-rst-username';
  private tokenCookieName = 'X-rst-session';
  private brandCookie;
  private xsrfToken;
  private brand;
  DOMAIN_IDP_MAPPING: string = 'localhost:2222-tbx2,da-surfdevkanoo.codegen.net:tbx2,da-surfdev.codegen.net:tbx1,localhost:1111-tbx2';

  constructor(
    private configLoader: ConfigLoader,
    private dataStore: DataStore,
    private httpNew: HttpClient,
    private injector: Injector,
    private cookieService: NGXCoockieServive,
    private logStore: LogStore,
    private surfUtilService: SurfUtilService,
    private surfRedirectUrlCaptureServiceService: SurfRedirectUrlCaptureServiceService,
    private surfVisibilityConfigService: SurfVisibilityConfigService
  ) {
    this.appContext = new AppContext();
    this.brandCookie = SurfCookieNames.BRAND_COOKIE;
    this.surfUtilService.clearCacheData();
  }

  public preLoadConfig() {
    const requestOptionsArgs: TCRequestOptions = new TCRequestOptions();
    requestOptionsArgs.headers = new HttpHeaders({'Content-Type': 'application/json'});
    const url = '/gateway/data/preLogin';
    return new Promise<boolean>((resolve, reject) => {
      return this.httpNew.get(url).subscribe(
        (data: any) => {
          if (data && data.status && data.status.code === 1) {
            const config: any = data.data[0];
            this.surfUtilService.addSessionData(SessionStorage.BL_PRE_LOGIN, JSON.stringify(config));
            if (config) {
              this.DOMAIN_IDP_MAPPING = config.DOMAIN_IDP_MAPPING;
              resolve(true); // Now this matches <boolean>
            }
          }
        },
        (error) => {
          reject(error); // Good practice to handle the error case
        }
      );
    });  }

  public checkValid() {
    const requestOptionsArgs: TCRequestOptions = new TCRequestOptions();
    requestOptionsArgs.headers = new HttpHeaders({'Content-Type': 'application/json'});
    const url = '/gateway/authorize/valid';
    return new Promise((resolve, reject) => {
      return this.httpNew.get(url).subscribe(
        (data: any) => {
          resolve(true);
        },
        (error) => {
          this.surfRedirectUrlCaptureServiceService.addRedirectData(window.location.href);
          this.userLogout();
        }
      );
    });
  }

  public loadConfigurations() {
    return new Promise((resolve, reject) => {
      this.configLoader.loadFromBaseConfig(environment.base_config)
          .then(
            resp => {
              // load scaffolding config
              const results: Array<Scaffolding> = this.configLoader
                                                      .configurations
                                                      .get('SCAFFOLDING_CONFIG');
              this.scaffStructure.next(results);

              resolve(true);
            },
            (error) => {
              this.userLogout();
            }
          );
    });
  }

  public async loadVisibilityConfigurations() {
    return new Promise(async (resolve, reject) => {
      try {
        await this.surfVisibilityConfigService.loadVisibilityConfigs();
        resolve(true);
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  }

  public getXsrfTokenVal(): string {
    return this.xsrfToken;
  }

  public setXsrfTokenVal(token: string) {
    this.xsrfToken = token;
  }

  public captureUserCookies() {
    const authenticationService = this.injector.get(SurfAuthenticationService);
    let devMode = false;
    if (!environment.production) {
      devMode = true;
    }
    authenticationService.captureUserCookies(devMode);
  }

  public userLogin() {
    const authenticationService = this.injector.get(SurfAuthenticationService);
    this.DOMAIN_IDP_MAPPING = this.configLoader.getModuleConfig(
      Configurations.DOMAIN_IDP_MAPPING,
      TC.MODULE_NAME.SURF_B2B
    );
    let devMode = false;
    if (!environment.production) {
      devMode = true;
    }
    if (authenticationService.userLogin(devMode)) {
      if (authenticationService.getXsrfToken()) {
        this.setXsrfTokenVal(authenticationService.getXsrfToken());
      }
      let username = null;
      if (this.cookieService.get('X-rst-username')) {
        username = this.cookieService.get('X-rst-username');
      }
      if (this.cookieService.get(this.brandCookie)) {
        this.setBrand(this.cookieService.get(this.brandCookie));
      }

    } else {
      this.surfRedirectUrlCaptureServiceService.addRedirectData(window.location.href);
      this.userLogout();
    }
  }

  public userLogout(): void {
    const authenticationService = this.injector.get(SurfAuthenticationService);
    this.setClientId(authenticationService);
    authenticationService.logoutUser('/gateway/authorize/logout', null, null);
  }

  setClientId(authenticationService: SurfAuthenticationService) {
    // Get the current host
    const currentHost = window.location.host;

    // Split by comma to get individual mappings
    const mappings = this.DOMAIN_IDP_MAPPING.split(',');

    let selectedIdp = null;

    // Loop through the mappings to find a match
    // Loop through the mappings to find a match
    for (const mapping of mappings) {
      const idpMap = mapping.split('~');

      // Check if the current host is included in the mapping
      if (currentHost===idpMap[0]) {
          selectedIdp = idpMap[1]; // Set the selected IDP
          break;
      }
    }

    if (selectedIdp) {
        // Call setClientTBX method before logging out
        authenticationService.setClientTBX(selectedIdp);
    }
  }


  /**
   * Load method permissions
   */
  public loadPermissions() {
    let url = this.configLoader.configurations.get(TC.CONF.CONF_ENDPOINT)[TC.ENDPOINT_ID.USER_RESTRICTIONS];
    const journey = this.getParameterByName('journey', window.location.href);
    let services = [];
    if (journey) {
      services = this.configLoader.conf([TC.CONF.CONF_JOURNEYS, journey.toUpperCase(), 'services']);
    }

    if (environment.mock) {
      url = url + '.mockdata.json';
    }

    return new Promise((resolve, reject) => {
      return this.httpNew.get(url, {
        params: {
          services: (services && Array.isArray(services) && services.length > 0) ? JSON.stringify(services) : null,
          modules: TC.MODULE_NAME.RESERVATION_SERVICE,
        }
      }).subscribe(
        (data: any) => {
          console.log(data);
          if (data && data.status && data.status.code === 1) {
            // this.dataStore.set(DataKey.moduleMethodPermissions, data.data.moduleMethodPermissions);
            this.dataStore.set('methodPermissions', data.data, false);
            resolve(true);
          }
        },
        (error) => {
          this.userLogout();
        }
      );
    });
  }

  public loadExternalScripts() {
    try {
      this.surfUtilService.loadExternalScripts();
    } catch (e) {
      console.log( 'Error Loading External Scripts', e);
    }
  }

  /**
   * Load user details fro jwt token
   */
  public loadUserDetail() {
    this.currentURL = window.location.href;
    const authenticationService = this.injector.get(SurfAuthenticationService);
    const session = authenticationService.getSession(this.currentURL);
    const userDetail = authenticationService.getUserFromJWT(session);
    if (!userDetail || !userDetail.user) {
      window.location.href  = authenticationService.getLoginPageUrl(this.DOMAIN_IDP_MAPPING);
    }
    this.dataStore.set(DataKey.userDetail, userDetail.user, true);
    return true;
  }

  /**
   * Load User Details from API
   */
  public loadUserDetailFromAPI() {
    this.currentURL = window.location.href;
    const authenticationService = this.injector.get(SurfAuthenticationService);
    let url = this.configLoader.configurations.get(TC.CONF.CONF_ENDPOINT)[TC.ENDPOINT_ID.AUTH_USER_DETAIL];
    let username;
    if (this.currentURL && this.currentURL.includes(this.userNameCookie)) {
      const urlParams = new URLSearchParams(window.location.search);
      let userNameParam = urlParams.get(this.userNameCookie);
      username = userNameParam;
    } else if (this.cookieService.get(this.userNameCookie) || this.cookieService.get(this.userNameCookie) != '') {
      username = this.cookieService.get(this.userNameCookie);
    }
    if (!username) {
      //window.location.href  = authenticationService.getLoginPageUrl(this.DOMAIN_IDP_MAPPING);
      //TODO make url config
      url = '/gateway/authorize/userInfo';
    } else {
      url = url + '/' + username;
    }

    return new Promise((resolve, reject) => {
      return this.httpNew.get(url).subscribe(
        (data: any) => {
          if (data && data.status && data.status.code === 1) {
            let user: User = data.data[0];
            if (user && user.id && user.userSummary && user.userSummary.username) {
              this.dataStore.set(DataKey.userDetail, user, true);
              resolve(true);
              const userDetail = this.dataStore.get(DataKey.userDetail).getValue();
              this.surfUtilService.addSessionData(SessionStorage.BL_LOGGED_USER_DETAILS,JSON.stringify(userDetail) )
              if (!userDetail || !userDetail.b2bUser) {
                if (this.cookieService.get(this.userNameCookie)) {
                  this.cookieService.delete(this.userNameCookie);
                }
                if (this.cookieService.get(this.tokenCookieName)) {
                  this.cookieService.delete(this.tokenCookieName);
                }
                if (!userDetail) {
                  this.userLogout();
                }
              }
            }
          } else {
            window.location.href = authenticationService.getLoginPageUrl(this.DOMAIN_IDP_MAPPING);
          }
        },
        (error) => {
          window.location.href = authenticationService.getLoginPageUrl(this.DOMAIN_IDP_MAPPING);
        }
      );
    });
  }

  loadClient() {
    let url = this.configLoader.configurations.get(TC.CONF.CONF_ENDPOINT)[TC.ENDPOINT_ID.GET_CLIENT];
    if (this.dataStore.get(DataKey.userDetail)) {
      const userDetail = this.dataStore.get(DataKey.userDetail).getValue();
      const requestOptionsArgs: TCRequestOptions = new TCRequestOptions();
      requestOptionsArgs.headers = new HttpHeaders({'Content-Type': 'application/json'});
      url = url + '/' + userDetail.clientId;
      return new Promise((resolve, reject) => {
        return this.httpNew.get(url).subscribe(
          (data: any) => {
            if (data && data.status && data.status.code === 1) {
              const client: any = data.data[0];
              if (client) {
                this.dataStore.set(DataKey.tradeClient, client, true);
                this.surfUtilService.addSessionData(SessionStorage.BL_LOGGED_USER_CLIENT, JSON.stringify(client));
                resolve(true);
              }
            }
          },
          (error) => {
            this.userLogout();
          }
        );
      });
    }
  }

  loadEmuClient() {
    if (sessionStorage.getItem('emulating') && sessionStorage.getItem('emulatingTCProfile')) {
      let url = this.configLoader.configurations.get(TC.CONF.CONF_ENDPOINT)[TC.ENDPOINT_ID.GET_CLIENT];
      const emuClientId = sessionStorage.getItem('emulatingTCProfile');
      const requestOptionsArgs: TCRequestOptions = new TCRequestOptions();
      requestOptionsArgs.headers = new HttpHeaders({'Content-Type': 'application/json'});
      url = url + '/' + emuClientId;
      return new Promise((resolve, reject) => {
        return this.httpNew.get(url).subscribe(
          (data: any) => {
            if (data && data.status && data.status.code === 1) {
              const client: any = data.data[0];
              if (client) {
                this.dataStore.set(DataKey.emulatingTCProfile, client, true);
                this.dataStore.set(DataKey.emulating, true);
                resolve(true);
              }
            }
          },
          (error) => {
            this.userLogout();
          }
        );
      });
    }
  }

  public loadModuleConfigurations() {
    const url = this.configLoader.configurations.get(TC.CONF.CONF_ENDPOINT)[TC.ENDPOINT_ID.MODULE_CONFIGURATIONS];

    const requestOptionsArgs: TCRequestOptions = new TCRequestOptions();
    requestOptionsArgs.headers = new HttpHeaders({'Content-Type': 'application/json'});
    requestOptionsArgs.params = new HttpParams();
    requestOptionsArgs.params = requestOptionsArgs.params.append('module', TC.MODULE_NAME.SURF_B2B);

    return new Promise((resolve, reject) => {
        this.configLoader.loadModuleConfigurations(url, TC.MODULE_NAME.SURF_B2B, requestOptionsArgs)
            .then(
              data => {
                this.dataStore.set(DataKey.moduleConfiguration, data);
                this.surfUtilService.addSessionData(SessionStorage.BL_MODULE_CONFIGS,JSON.stringify(data));
                this.surfVisibilityConfigService.moduleConfigBasedVisibilitySetup([
                  { code: 'ENABLED_EXTENDED_TOUR_SEARCH_CRITERIA', component: 'search_tour_criteria' }
                ]);
                resolve(true);
              },
              (error) => {
                this.userLogout();
              }
            );
      }
    );
  }

  public loadLoggerConfigs() {
    if (this.configLoader.configurations.get(TC.CONF.CONF_LOG_CONFIG)) {
      this.surfUtilService.addSessionData(SessionStorage.BL_LOGGER_CONF, JSON.stringify(this.configLoader.configurations.get(TC.CONF.CONF_LOG_CONFIG)));
    }
  }

  private getParameterByName(name, url) {
    if (!url) {
      url = window.location.href;
    }
    name = name.replace(/[\[\]]/g, '\\$&');
    let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'), results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  public getBrand() {
    return this.brand;
  }

  public setBrand(value) {
    this.brand = value;
  }

  public async loadPaxTypeAges() {
    const promise = new Promise<void>((resolve) => {
      this.injector.get(DataServiceHandler).getPaxSubTypeAges().subscribe(
        res => {
          if (this.injector.get(CommonHelper).dataValidity(res)) {
            const paxTypeAges: any[] = res[0].paxTypes;
            this.surfUtilService.addSessionData(SessionStorage.BL_PAX_TYPE_AGES, JSON.stringify(paxTypeAges));
            SurfGuestDetailsUtil.MAX_CHILD_AGE = Math.floor(paxTypeAges.find(paxTypeAge => paxTypeAge.code ===
              'C').age.upper / 12);
            SurfGuestDetailsUtil.MAX_TEEN_AGE = Math.floor(paxTypeAges.find(paxTypeAge => paxTypeAge.code ===
              'T').age.upper / 12);
            if (paxTypeAges.findIndex(paxTypeAge => paxTypeAge.code === 'T') > -1) {
              SurfGuestDetailsUtil.TEEN_AGE_AVAILABLE = true;
            }
            SurfGuestDetailsUtil.MAX_INFANT_AGE = paxTypeAges.find(paxTypeAge => paxTypeAge.code === 'I').age.upper;
            SurfGuestDetailsUtil.MIN_ADULT_AGE = paxTypeAges.find(paxTypeAge => paxTypeAge.code === 'A').age.lower /
              12;
            resolve();
          } else if (res instanceof TcApiError || res instanceof TcHttpError) {
            resolve();
          }
        },
        error => {
          resolve();
          console.log('Error in getting pax sub type ages');
        }
      );
    });
    return promise;

   /* if (sessionStorage.getItem(SessionStorage.BL_PAX_TYPE_AGES)) {
      const paxTypeAges = JSON.parse(sessionStorage.getItem(SessionStorage.BL_PAX_TYPE_AGES));
      this.dataStore.set(DataKey.moduleConfiguration, paxTypeAges);
      SurfGuestDetailsUtil.MAX_CHILD_AGE = Math.floor(paxTypeAges.find(paxTypeAge => paxTypeAge.code ===
        'C').age.upper / 12);
      SurfGuestDetailsUtil.MAX_TEEN_AGE = Math.floor(paxTypeAges.find(paxTypeAge => paxTypeAge.code === 'T').age.upper /
        12);
      if (paxTypeAges.findIndex(paxTypeAge => paxTypeAge.code === 'T') > -1) {
        SurfGuestDetailsUtil.TEEN_AGE_AVAILABLE = true;
      }
      SurfGuestDetailsUtil.MAX_INFANT_AGE = paxTypeAges.find(paxTypeAge => paxTypeAge.code === 'I').age.upper;
      SurfGuestDetailsUtil.MIN_ADULT_AGE = paxTypeAges.find(paxTypeAge => paxTypeAge.code === 'A').age.lower / 12;
    }*/
  }
}
